import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { PageLayout } from '../../../PageLayout';
import { UpstackDataGrid } from '../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { ApiAdvancedFilter, PaginatedResponse, blobToExcel } from '../../../../store/GeneralStore';
import {
  AssetRevenueAttribution,
  fetchAssetRevenueAttributions,
  getAssetRevenueAttributionsSubject
} from '../../../../store/Attribution';
import { downloadAttribution } from '../../../../Api/Attribution/AttributionApi';
import { DefaultAssetRevenueAttributionGridColumnDefs } from './GridColumnDefs';
import { getCustomizationSubject, UserCustomizations } from '../../../../store/UserCustomizations';
import { buildAdvancedFilterSet, getInitialState } from '../../../Compounds/UpstackDataGrid/helpers';
import { ComponentSpinner } from '../../../Compounds/Loading/ComponentSpinner';
import { Card } from '../../../Atoms/Card';
import { Run, RunStatus, getRunsLoadingSubject } from '../../../../store/Run';
import { RunSelection } from '../../../Compounds/Run/RunSelection';
import { ProcessingRunBanner } from '../../../Compounds/Run/ProcessingRunBanner';
import { Text } from '../../../Atoms/Text';
import { Icon } from '../../../Atoms/Icon';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { getFormattedDate } from '../../../../utils/helpers';

const GRID_PAGE = 'assetRevenueAttributionGrid' as const;
const EXPORT_READY_STATUSES = [RunStatus.PENDING_LOCK, RunStatus.LOCKED];
const EXPORT_READY_STATUSES_TEXT = EXPORT_READY_STATUSES.join(' or ');

export const AssetRevenueAttributionPage = () => {
  const [selectedRun, setSelectedRun] = useState<Run | null>(null);
  const [runsLoading, setRunsLoading] = useState(true);
  const [gridLoading, setGridLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [attributions, setAttributions] = useState<PaginatedResponse<AssetRevenueAttribution>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations>();
  const { setSnack } = useSnackbar();

  const columns = useMemo(() => DefaultAssetRevenueAttributionGridColumnDefs(), []);
  const filterKeys = useMemo(
    () => columns.filter((column) => column.filterable).map((column) => column.field),
    [columns]
  );

  const isDownloadReady = !!selectedRun && EXPORT_READY_STATUSES.includes(selectedRun.status);

  const handleDownload = async () => {
    if (!selectedRun) return;

    setIsDownloading(true);
    const { data, error } = await downloadAttribution(selectedRun.id);
    if (error) {
      setSnack({ message: 'Failed to generate export', type: 'error', open: true });
    } else if (data) {
      const fileName = `Tally_Attribution_${getFormattedDate(selectedRun.date, 'YYYY MM').replace(' ', '_')}.xlsx`;
      blobToExcel(data, fileName);
    }
    setIsDownloading(false);
  };

  const latestFetchGeneration = useRef(0);

  const handleFetchAssetRevenueAttributions = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
      if (!selectedRun) return;
      const generation = ++latestFetchGeneration.current;
      if (localLoading) {
        setGridLoading(true);
        setAttributions(undefined);
      }
      await fetchAssetRevenueAttributions(selectedRun.id, filters, forceRefresh);
      if (localLoading && generation === latestFetchGeneration.current) setGridLoading(false);
    },
    [selectedRun]
  );

  const getInitialSort = useCallback((userCust: UserCustomizations): GridSortModel => {
    const savedSort = userCust?.content?.[GRID_PAGE]?.muiConfig?.sorting?.sortModel;
    return savedSort?.length ? savedSort : [];
  }, []);

  const getInitialFilter = useCallback((userCust: UserCustomizations): GridFilterModel => {
    return userCust?.content?.[GRID_PAGE]?.muiConfig?.filter?.filterModel || ({} as GridFilterModel);
  }, []);

  useEffect(() => {
    const loadingSub = getRunsLoadingSubject().subscribe((loading) => setRunsLoading(loading));
    const attributionsSub = getAssetRevenueAttributionsSubject().subscribe((a) => setAttributions(a));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));

    return () => {
      loadingSub.unsubscribe();
      attributionsSub.unsubscribe();
      userCustSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!userCustomizations || !selectedRun) return;

    const initialSortModel = getInitialSort(userCustomizations);
    const initialFilterModel = getInitialFilter(userCustomizations);
    handleFetchAssetRevenueAttributions(
      {
        ...buildAdvancedFilterSet(initialFilterModel, filterKeys),
        orders: initialSortModel.length
          ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
          : undefined
      },
      true,
      true
    );
  }, [
    userCustomizations,
    selectedRun,
    handleFetchAssetRevenueAttributions,
    filterKeys,
    getInitialSort,
    getInitialFilter
  ]);

  const gridSettings = userCustomizations?.content?.[GRID_PAGE];
  const initialState = gridSettings ? getInitialState(gridSettings, columns, GRID_PAGE) : undefined;

  return (
    <PageLayout
      className="w-full min-h-[85vh] mt-1"
      dataCy="asset-revenue-attribution-page"
      dataTestId="asset-revenue-attribution-page"
      pageTitle="Asset Revenue Attribution">
      <Card className="mt-2">
        <div className="w-full p-4">
          <ProcessingRunBanner
            run={selectedRun}
            className="mb-4">
            Export to CaptivateIQ stays disabled until the run is {EXPORT_READY_STATUSES_TEXT}.
          </ProcessingRunBanner>
          {runsLoading ? (
            <ComponentSpinner label="Loading runs..." />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
              <RunSelection
                selectedRunId={selectedRun?.id || null}
                setSelectedRun={setSelectedRun}
                types={[RunStatus.PROCESSING, RunStatus.PENDING_LOCK, RunStatus.LOCKED]}
              />
              <Tooltip
                title={
                  !isDownloadReady && !isDownloading
                    ? `Export is only available once the run is ${EXPORT_READY_STATUSES_TEXT}`
                    : ''
                }
                placement="left">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 2,
                    px: 1,
                    height: 56,
                    width: 'fit-content',
                    borderRadius: '6px',
                    opacity: !isDownloadReady && !isDownloading ? 0.45 : 1,
                    backgroundColor: !isDownloadReady && !isDownloading ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                    cursor: !isDownloadReady || isDownloading ? 'default' : 'pointer',
                    transition: 'opacity 0.2s, background-color 0.2s',
                    '&:hover': {
                      '& .download-text': {
                        opacity: !isDownloadReady || isDownloading ? 1 : 0.7
                      },
                      '& .xlsxdownload': {
                        borderRadius: '3px',
                        backgroundColor: !isDownloadReady || isDownloading ? 'transparent' : 'rgba(0, 0, 0, 0.05)'
                      }
                    }
                  }}
                  onClick={!isDownloadReady || isDownloading ? undefined : handleDownload}>
                  {isDownloading ? (
                    <CircularProgress
                      size={24}
                      sx={{ mx: 1 }}
                    />
                  ) : (
                    <IconButton
                      disabled={!isDownloadReady}
                      title="Export to CaptivateIQ"
                      sx={{
                        p: 1,
                        pr: 0,
                        pl: 2,
                        pointerEvents: 'none'
                      }}>
                      <Icon type="xlsxdownload" />
                    </IconButton>
                  )}
                  <Text className="download-text">Export to CaptivateIQ</Text>
                </Box>
              </Tooltip>
            </Box>
          )}
          {!runsLoading && (
            <div className="max-w-[60vw] min-w-full">
              {!selectedRun || !initialState || gridLoading ? (
                <div className="flex h-[45rem] m-auto">
                  <ComponentSpinner label="Loading data..." />
                </div>
              ) : (
                <UpstackDataGrid
                  title="Asset Revenue Attribution"
                  rows={attributions?.data || []}
                  columns={columns}
                  initialState={initialState}
                  page={GRID_PAGE}
                  gridSettings={gridSettings}
                  showSearch={false}
                  apiFilter
                  fetchFunction={handleFetchAssetRevenueAttributions}
                  rowCount={attributions?.totalCount}
                  rowPrimaryKey="asset_upstack_id"
                  advancedFiltering
                  filterKeys={filterKeys}
                  withoutSave
                  hideSaveView
                  hideExport
                  customNoRowsMessage="No attribution rows available"
                />
              )}
            </div>
          )}
        </div>
      </Card>
    </PageLayout>
  );
};
